import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { mergeMap, take, concatMap } from 'rxjs/operators';

import { DeactivationGuardedComponent } from '@shared/models/component';
import { UnsavedChangesDialogComponent } from '@shared/components';
import { UnsavedChangesDialogOptions } from '@shared/enums';
import { Constants } from '@shared/utils';
import { CanDeactivateFn } from '@shared/models/guard';

export const ignoreNextGuardCall$ = new BehaviorSubject<boolean>(false);

export const canDeactivateUnsavedChangesGuard: CanDeactivateFn<
  DeactivationGuardedComponent
> = (component: DeactivationGuardedComponent): Observable<boolean> => {
  const dialog = inject(MatDialog);

  return canDeactivateUnsavedChanges(component, dialog);
};

export const canDeactivateUnsavedChanges = (
  component: DeactivationGuardedComponent,
  dialog: MatDialog
) => {
  return ignoreNextGuardCall$.asObservable().pipe(
    take(1),
    concatMap((shouldByPassGuard) => {
      if (shouldByPassGuard) {
        ignoreNextGuardCall$.next(false);
        return of(true);
      }

      return openUnsavedChangesModal(dialog, component);
    })
  );
};

const openUnsavedChangesModal = (
  dialog: MatDialog,
  component: DeactivationGuardedComponent
): Observable<boolean> => {
  if (!component?.canDeactivate) {
    return of(true);
  }

  if (!component?.canDeactivate()) {
    const dialogRef = dialog.open(UnsavedChangesDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.unsavedChangesGuard.titleUnsavedChanges:Unsaved changes`,
        // eslint-disable-next-line max-len
        message: $localize`:@@shared.unsavedChangesGuard.txtUnsavedChangesWillBeLost:Unsaved changes will be lost if you don't save. Press Cancel to stay on the page, or press Save to save your changes and continue.`,
        saveButtonLabel: $localize`:@@shared.unsavedChangesGuard.btnSave:Save`,
      },
    });

    return dialogRef.afterClosed().pipe(
      mergeMap((result) => {
        switch (result) {
          case UnsavedChangesDialogOptions.Continue:
            return of(true);
          case UnsavedChangesDialogOptions.Save:
            return component.save();
          case UnsavedChangesDialogOptions.Cancel:
            return of(false);
          default:
            return of(false);
        }
      })
    );
  }

  return of(true);
};
